import { createContext, useContext, useState } from "react";

import {
  DEFAULT_RANGE_PRESET,
  RANGE_PRESETS,
  computePresetRange,
  matchPreset,
  type RangePresetKey,
} from "./dateRangePresets";
import type { GlobalFilters } from "./globalFilters";

// The clicked preset is remembered for this tab so a page refresh keeps the
// chip the user picked. This matters when two presets resolve to the same
// window (e.g. the 7th of a month, where "7D" and "This month" are identical):
// without the memory, matchPreset would re-resolve the refreshed URL to the
// higher-precedence chip ("This month") and silently flip the selection. It is
// only honoured while the URL still carries that window, so a fresh visit (no
// range in the URL) still opens on month-to-date regardless of the stored pick.
const RANGE_PRESET_STORAGE_KEY = "pageFilters:dashboard:range";

function readStoredPreset(): RangePresetKey | null {
  if (typeof window === "undefined") {
    return null;
  }
  try {
    const raw = window.sessionStorage.getItem(RANGE_PRESET_STORAGE_KEY);
    return RANGE_PRESETS.some((preset) => preset.key === raw)
      ? (raw as RangePresetKey)
      : null;
  } catch {
    return null;
  }
}

function writeStoredPreset(key: RangePresetKey): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(RANGE_PRESET_STORAGE_KEY, key);
  } catch {
    // Best-effort: a private-mode browser just falls back to precedence matching.
  }
}

// Dashboard-scoped filter state (L0-U16 follow-up). The dashboard widgets and
// the preset bar used to each read the raw URL date filter, so the month-to-date
// default was applied by a post-mount effect -- leaving a one-frame window where
// the grid painted all-time data and the "All" chip before the URL caught up.
// Resolving the effective window synchronously here (defaulting to month-to-date
// when the URL has no range) removes that flash: widgets render the correct
// window on the first paint. The explicit in-session pick is React state, so it
// resets on remount -- leaving the dashboard and returning always reopens on the
// current month rather than the previously chosen window.
export interface DashboardFilters {
  // Effective filters the widgets query against; the date window is defaulted to
  // month-to-date whenever the URL carries no range.
  filters: GlobalFilters;
  // The preset chip to highlight ("custom" when the window matches none).
  activePreset: RangePresetKey | "custom";
  // Narrow the dashboard to a preset. Writes the concrete window to the URL so a
  // refresh within the same visit keeps it; the in-session pick is also tracked
  // so two presets that resolve to the same window stay independently selectable.
  selectPreset: (key: RangePresetKey) => void;
}

export const DashboardFiltersContext = createContext<DashboardFilters | null>(
  null,
);

// Computes the dashboard's effective filters from its base (URL + remembered
// account/tag) filters. Called once at the page level; the result is shared with
// widgets via DashboardFiltersContext.
export function useDashboardFilterState(
  base: GlobalFilters,
  setBase: (patch: Partial<GlobalFilters>) => void,
): DashboardFilters {
  // The user's explicit in-session pick. Resets on remount so the date range is
  // deliberately not persisted across navigation.
  const [selectedKey, setSelectedKey] = useState<RangePresetKey | null>(null);
  // This tab's last pick, read once. Used ONLY to disambiguate a refresh (where
  // selectedKey has reset to null but the URL still carries the window) -- never
  // to override a fresh visit's month-to-date default.
  const [storedPreset] = useState<RangePresetKey | null>(() => readStoredPreset());

  const now = new Date();
  const hasUrlRange = base.since !== undefined || base.until !== undefined;

  let activePreset: RangePresetKey | "custom";
  if (selectedKey !== null) {
    // Honour the explicit in-session pick while its window still matches the
    // active filter (covers "All", whose empty window matches an empty URL, and
    // disambiguates two presets that resolve to the same window).
    const picked = computePresetRange(selectedKey, now);
    activePreset =
      base.since === picked.since && base.until === picked.until
        ? selectedKey
        : matchPreset(base, now);
  } else if (hasUrlRange) {
    // A range is in the URL with no in-session pick (refresh / deep link /
    // drill-through). Prefer this tab's remembered pick while its window still
    // matches -- that keeps the clicked chip across a refresh when two presets
    // resolve to the same window. Otherwise fall back to precedence matching.
    if (storedPreset !== null) {
      const picked = computePresetRange(storedPreset, now);
      activePreset =
        base.since === picked.since && base.until === picked.until
          ? storedPreset
          : matchPreset(base, now);
    } else {
      activePreset = matchPreset(base, now);
    }
  } else {
    // Fresh visit (no range in the URL): default to month-to-date synchronously,
    // ignoring any stale stored pick so returning always reopens on this month.
    activePreset = DEFAULT_RANGE_PRESET;
  }

  const resolved =
    activePreset === "custom"
      ? { since: base.since, until: base.until }
      : computePresetRange(activePreset, now);

  const filters: GlobalFilters = {
    ...base,
    since: resolved.since,
    until: resolved.until,
  };

  const selectPreset = (key: RangePresetKey) => {
    setSelectedKey(key);
    writeStoredPreset(key);
    const range = computePresetRange(key, now);
    setBase({ since: range.since, until: range.until });
  };

  return { filters, activePreset, selectPreset };
}

export function useDashboardFilters(): DashboardFilters {
  const ctx = useContext(DashboardFiltersContext);
  if (ctx === null) {
    throw new Error(
      "useDashboardFilters must be used within a DashboardFiltersContext provider",
    );
  }
  return ctx;
}
